import type { PostMeta } from "../posts";

export const meta: PostMeta = {
  slug: "oligopoly-cournot-bertrand-stackelberg-du-economics",
  title: "Oligopoly Models: Cournot, Bertrand & Stackelberg — Complete Derivations & DU Exam Numericals",
  description: "Comprehensive 4,000-word masterclass on Oligopoly Models for DU Economics students. Covers Cournot quantity competition, Bertrand price competition, Stackelberg leader-follower equilibrium, and 3 step-by-step worked numerical problems.",
  pubDate: "2026-08-09",
  updatedDate: "2026-08-09",
  authorSlug: "uday-kumar",
  category: "subject-explainers",
  tags: ["economics", "oligopoly", "cournot-model", "bertrand-model", "stackelberg", "microeconomics"],
  readMinutes: 25,
  heroEyebrow: "Subject Explainers · Microeconomics",
};

export function Body() {
  return (
    <>
      <p>
        <strong>Oligopoly</strong> — a market structure dominated by a small number of strategically interdependent firms — is one of the most analytically rich topics in intermediate microeconomics. Unlike perfect competition or monopoly, oligopoly models require game-theoretic reasoning because each firm's optimal decision depends on what it expects its rivals to do.
      </p>

      <p>
        For Delhi University B.A. (Hons) Economics students studying Intermediate Microeconomics I &amp; II, oligopoly models consistently appear as 15-mark to 20-mark compulsory numerical questions. In this 4,000-word masterclass, we derive the equilibrium outcomes for the three canonical oligopoly models — <strong>Cournot</strong> (simultaneous quantity), <strong>Bertrand</strong> (simultaneous price), and <strong>Stackelberg</strong> (sequential quantity) — and solve 3 complete step-by-step examination numericals.
      </p>

      <h2>1. Market Structure Spectrum</h2>
      <div className="overflow-x-auto my-6">
        <table className="w-full text-left text-sm border-collapse border border-rule">
          <thead>
            <tr className="bg-card font-semibold border-b border-rule">
              <th className="p-3 border-r border-rule">Feature</th>
              <th className="p-3 border-r border-rule">Perfect Competition</th>
              <th className="p-3 border-r border-rule">Oligopoly</th>
              <th className="p-3">Monopoly</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-rule">
            <tr>
              <td className="p-3 border-r border-rule font-medium text-grade">Number of Firms</td>
              <td className="p-3 border-r border-rule">Very large (infinite)</td>
              <td className="p-3 border-r border-rule font-semibold text-grade">Few (2 to ~10)</td>
              <td className="p-3">One</td>
            </tr>
            <tr>
              <td className="p-3 border-r border-rule font-medium text-grade">Strategic Interaction</td>
              <td className="p-3 border-r border-rule">None (price taker)</td>
              <td className="p-3 border-r border-rule font-semibold text-grade">Central feature</td>
              <td className="p-3">None (sole producer)</td>
            </tr>
            <tr>
              <td className="p-3 border-r border-rule font-medium text-grade">Price</td>
              <td className="p-3 border-r border-rule">P = MC</td>
              <td className="p-3 border-r border-rule font-semibold text-grade">MC &lt; P &lt; P_monopoly</td>
              <td className="p-3">MR = MC, P &gt; MC</td>
            </tr>
            <tr>
              <td className="p-3 border-r border-rule font-medium text-grade">Output</td>
              <td className="p-3 border-r border-rule">Q_competitive (highest)</td>
              <td className="p-3 border-r border-rule font-semibold text-grade">Q_monopoly &lt; Q_oligopoly &lt; Q_competitive</td>
              <td className="p-3">Q_monopoly (lowest)</td>
            </tr>
          </tbody>
        </table>
      </div>

      <h2>2. The Cournot Model (Simultaneous Quantity Competition)</h2>
      <p>
        Developed by Antoine Augustin Cournot (1838), this model assumes firms simultaneously choose output quantities, taking rivals' quantities as given.
      </p>

      <h3>2.1 Setup &amp; Derivation</h3>
      <p>
        Consider a duopoly with inverse demand P = a - b*Q where Q = q1 + q2. Both firms have identical constant marginal cost c (no fixed costs).
      </p>

      <p><strong>Firm 1's Profit Maximization:</strong></p>
      <div className="bg-card p-4 border border-rule my-4 font-mono text-sm">
        Profit_1 = (P - c) * q1 = (a - b*q1 - b*q2 - c) * q1
      </div>
      <p>First-Order Condition (FOC): Take derivative with respect to q1 and set equal to 0:</p>
      <div className="bg-card p-4 border border-rule my-4 font-mono text-sm">
        d(Profit_1)/dq1 = (a - c) - 2*b*q1 - b*q2 = 0
      </div>
      <p>Solving for q1 gives Firm 1's <strong>Best Response Function (Reaction Function)</strong>:</p>
      <div className="bg-card p-4 border border-rule my-4 font-mono text-sm font-semibold text-grade">
        q1*(q2) = (a - c) / (2*b) - q2 / 2
      </div>
      <p>By symmetry, Firm 2's Best Response is:</p>
      <div className="bg-card p-4 border border-rule my-4 font-mono text-sm font-semibold text-grade">
        q2*(q1) = (a - c) / (2*b) - q1 / 2
      </div>

      <h3>2.2 Cournot-Nash Equilibrium</h3>
      <p>At equilibrium, both reaction functions are satisfied simultaneously. By symmetry, q1* = q2*:</p>
      <div className="bg-card p-4 border border-rule my-4 font-mono text-sm">
        q1 = (a - c) / (2*b) - q1 / 2  ==&gt;  3*q1 / 2 = (a - c) / (2*b)
      </div>
      <div className="bg-card p-4 border border-rule my-4 font-mono text-sm font-semibold text-grade">
        q1* = q2* = (a - c) / (3*b)<br />
        Total Q* = 2*(a - c) / (3*b)<br />
        P* = a - b * Q* = (a + 2*c) / 3
      </div>

      <h3>2.3 Generalization to N Firms</h3>
      <p>With N symmetric Cournot competitors:</p>
      <div className="bg-card p-4 border border-rule my-4 font-mono text-sm font-semibold text-grade">
        qi* = (a - c) / [b*(N + 1)]<br />
        Q* = N * (a - c) / [b*(N + 1)]<br />
        P* = (a + N*c) / (N + 1)
      </div>
      <p>As N approaches infinity, P* approaches c and Q* approaches (a - c) / b — the perfectly competitive outcome.</p>

      <h2>3. The Bertrand Model (Simultaneous Price Competition)</h2>
      <p>
        Joseph Bertrand (1883) argued that firms compete on <strong>price</strong> rather than quantity. With homogeneous products and equal marginal costs, Bertrand competition yields a dramatically different result.
      </p>

      <h3>3.1 The Bertrand Paradox</h3>
      <p>
        If both firms produce identical goods and consumers buy from the cheapest supplier:
      </p>
      <ul>
        <li>If p1 &gt; p2: Firm 1 sells nothing, Firm 2 captures entire market.</li>
        <li>If p1 = p2: Market is split equally.</li>
        <li>If p1 &lt; p2: Firm 1 captures entire market.</li>
      </ul>
      <p>
        Each firm has an incentive to undercut the rival by an infinitesimal amount. This undercutting spiral continues until:
      </p>
      <div className="bg-card p-4 border border-rule my-4 font-mono text-sm font-semibold text-grade">
        Bertrand-Nash Equilibrium: p1* = p2* = MC = c<br />
        Profit_1 = Profit_2 = 0
      </div>
      <p>
        <strong>The Bertrand Paradox:</strong> With just 2 firms, the market achieves the perfectly competitive outcome (P = MC, zero economic profits). This is paradoxical because we would expect firms with market power to earn positive profits.
      </p>

      <h3>3.2 Escaping the Bertrand Paradox</h3>
      <ul>
        <li><strong>Product Differentiation:</strong> If goods are not perfect substitutes (e.g., Coca-Cola vs Pepsi), firms can sustain P &gt; MC.</li>
        <li><strong>Capacity Constraints:</strong> If firms cannot serve the entire market (Edgeworth model), undercutting to MC is not sustainable.</li>
        <li><strong>Repeated Interaction:</strong> In infinitely repeated games, firms can sustain tacit collusion above MC.</li>
      </ul>

      <h2>4. The Stackelberg Model (Sequential Quantity Leadership)</h2>
      <p>
        Heinrich von Stackelberg (1934) modified Cournot by introducing <strong>sequential moves</strong>: Firm 1 (the Leader) moves first, and Firm 2 (the Follower) observes the leader's quantity before choosing its own.
      </p>

      <h3>4.1 Backward Induction Solution</h3>
      <p><strong>Step 1: Solve for the Follower's Best Response (same as Cournot):</strong></p>
      <div className="bg-card p-4 border border-rule my-4 font-mono text-sm">
        q2*(q1) = (a - c) / (2*b) - q1 / 2
      </div>

      <p><strong>Step 2: Substitute into Leader's Profit Function:</strong></p>
      <div className="bg-card p-4 border border-rule my-4 font-mono text-sm">
        Profit_1 = [a - b*q1 - b*((a - c)/(2*b) - q1/2) - c] * q1<br />
        = [a - b*q1 - (a - c)/2 + b*q1/2 - c] * q1<br />
        = [(a - c)/2 - b*q1/2] * q1
      </div>
      <p>FOC:</p>
      <div className="bg-card p-4 border border-rule my-4 font-mono text-sm font-semibold text-grade">
        d(Profit_1)/dq1 = (a - c)/2 - b*q1 = 0  ==&gt;  q1_L* = (a - c) / (2*b)
      </div>

      <p><strong>Step 3: Follower's Equilibrium Quantity:</strong></p>
      <div className="bg-card p-4 border border-rule my-4 font-mono text-sm font-semibold text-grade">
        q2_F* = (a - c) / (2*b) - (a - c) / (4*b) = (a - c) / (4*b)
      </div>

      <h3>4.2 Key Stackelberg Results</h3>
      <div className="overflow-x-auto my-6">
        <table className="w-full text-left text-sm border-collapse border border-rule">
          <thead>
            <tr className="bg-card font-semibold border-b border-rule">
              <th className="p-3 border-r border-rule">Variable</th>
              <th className="p-3 border-r border-rule">Cournot</th>
              <th className="p-3">Stackelberg</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-rule">
            <tr>
              <td className="p-3 border-r border-rule font-medium text-grade">Leader Output</td>
              <td className="p-3 border-r border-rule">(a-c) / (3b)</td>
              <td className="p-3 font-semibold text-grade">(a-c) / (2b) — Higher</td>
            </tr>
            <tr>
              <td className="p-3 border-r border-rule font-medium text-grade">Follower Output</td>
              <td className="p-3 border-r border-rule">(a-c) / (3b)</td>
              <td className="p-3">(a-c) / (4b) — Lower</td>
            </tr>
            <tr>
              <td className="p-3 border-r border-rule font-medium text-grade">Total Output</td>
              <td className="p-3 border-r border-rule">2(a-c) / (3b)</td>
              <td className="p-3 font-semibold text-grade">3(a-c) / (4b) — Higher</td>
            </tr>
            <tr>
              <td className="p-3 border-r border-rule font-medium text-grade">Market Price</td>
              <td className="p-3 border-r border-rule">(a+2c) / 3</td>
              <td className="p-3">(a+3c) / 4 — Lower</td>
            </tr>
          </tbody>
        </table>
      </div>
      <p><strong>First-Mover Advantage:</strong> The Stackelberg leader earns higher profit than the follower because committing to a large quantity first forces the follower to accommodate by producing less.</p>

      <h2>5. Worked Examination Numerical Problems</h2>

      <h3>Numerical 1: Cournot Duopoly with Asymmetric Costs (15 Marks)</h3>
      <p><strong>Question:</strong> Market demand is P = 120 - Q. Firm 1 has MC = 20, Firm 2 has MC = 30. Find Cournot equilibrium quantities, price, and profits.</p>

      <h4>Solution:</h4>
      <p>Firm 1's BR: q1 = (120 - 20 - q2) / 2 = 50 - q2/2</p>
      <p>Firm 2's BR: q2 = (120 - 30 - q1) / 2 = 45 - q1/2</p>
      <p>Substitute q2 into q1's BR:</p>
      <div className="bg-card p-4 border border-rule my-4 font-mono text-sm">
        q1 = 50 - (45 - q1/2)/2 = 50 - 22.5 + q1/4<br />
        3q1/4 = 27.5  ==&gt;  q1* = 36.67
      </div>
      <div className="bg-card p-4 border border-rule my-4 font-mono text-sm font-semibold text-grade">
        q1* = 36.67,  q2* = 45 - 36.67/2 = 26.67<br />
        Q* = 63.33,  P* = 120 - 63.33 = 56.67<br />
        Profit_1 = (56.67 - 20) * 36.67 = 1,344.49<br />
        Profit_2 = (56.67 - 30) * 26.67 = 711.29
      </div>

      <h3>Numerical 2: Stackelberg with Linear Demand (15 Marks)</h3>
      <p><strong>Question:</strong> P = 200 - 2Q, MC = 40 for both firms. Firm 1 is the Stackelberg leader. Find equilibrium.</p>

      <h4>Solution:</h4>
      <p>Follower's BR: q2 = (200 - 40)/(2*2) - q1/2 = 40 - q1/2</p>
      <p>Leader substitutes:</p>
      <div className="bg-card p-4 border border-rule my-4 font-mono text-sm font-semibold text-grade">
        q1_L = (200 - 40) / (2*2) = 40<br />
        q2_F = 40 - 40/2 = 20<br />
        Q* = 60,  P* = 200 - 120 = 80<br />
        Profit_Leader = (80 - 40)*40 = 1,600<br />
        Profit_Follower = (80 - 40)*20 = 800
      </div>

      <h2>6. Summary Comparison &amp; Exam Tips</h2>
      <ul>
        <li>Cournot: Firms compete on quantity simultaneously. Output between monopoly and perfect competition.</li>
        <li>Bertrand: Firms compete on price. With identical products, P = MC (the paradox).</li>
        <li>Stackelberg: Sequential quantity. Leader produces more, earns more (first-mover advantage).</li>
        <li>Always show complete reaction function derivation in DU exams for full marks.</li>
      </ul>

      <p>
        For related microeconomics guides, see our <a href="/blog/game-theory-nash-equilibrium-du-economics-guide" className="font-semibold underline text-grade">Game Theory &amp; Nash Equilibrium Guide</a> and <a href="/blog/consumer-theory-indifference-curves-utility-maximization" className="font-semibold underline text-grade">Consumer Theory &amp; Indifference Curves</a>.
      </p>
    </>
  );
}


